import { Api } from 'lib';
import { useEffect, useRef } from 'react';

/**
 * The language to save to the signed-in fan's account, or null when there is nothing to save.
 *
 * Emails read the language from the account (`User.language`), because a send happens on the
 * server, long after any browser has closed. The web app stopped writing it on 22 May (fafbdbc) so
 * that the stored value could not override the system language on sign-in. That fix removed both
 * directions; only reading back was the problem. So a fan who chose Japanese on the web got the
 * web in Japanese and every email banner in English.
 *
 * Only an explicit choice is saved (`languageManuallySet`), never the detected system language:
 * the account should hold what the fan said, not a guess about one device.
 *
 * Saved once per choice, not whenever the account disagrees. `languageSavedToAccount` records what
 * this device last saved, as `<account id>:<language>` so a second account on the same browser is
 * still saved. Comparing against the account instead would undo a later choice made in
 * the mobile app: the next web visit would see "account en, web ja" and switch the account back.
 */
export function languageToSave({ userId, language, languageManuallySet, languageSavedToAccount }) {
  if (!userId || !languageManuallySet || !language) return null;
  if (languageSavedToAccount === `${userId}:${language}`) return null;

  return language;
}

/**
 * Saves the fan's explicit language choice to their account, once per choice.
 *
 * Runs in the signed-in container, so it covers a choice made in Settings, one made on the login or
 * sign-up page before signing in, and one made before this existed (the choice is persisted on the
 * device). Nothing here reads the account's language back into the web app.
 */
export function useSaveLanguageChoice({ user, appState, dispatch }) {
  const inFlight = useRef(null);
  const language = languageToSave({
    userId: user?._id,
    language: appState?.language,
    languageManuallySet: appState?.languageManuallySet,
    languageSavedToAccount: appState?.languageSavedToAccount
  });

  useEffect(() => {
    if (!language) return;

    const key = `${user._id}:${language}`;
    if (inFlight.current === key) return;
    inFlight.current = key;

    Api.post('/users/changeLanguage', { language }, undefined, { silent: true }).then((res) => {
      inFlight.current = null;

      // A failure resolves (with `err`, or `false`) rather than throwing. Nothing is recorded then,
      // so the next visit tries again.
      if (!res || res.err) return;

      dispatch({ type: 'UPDATE_USER', payload: { language } });
      dispatch({ type: 'UPDATE_APP_STATE', payload: { languageSavedToAccount: key } });
    });
  }, [language, user?._id, dispatch]);
}
